
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import * as cssLinter from "css";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";

const enum fields {
	heading = "heading",
	info = "info",
	label = "label",
	link = "link",
	customCSS = "customCSS",
	openInNewWindow = "openInNewWindow",
	variant = "variant",
}

interface OwnProps extends WidgetProps {
	fields: {
		[fields.label]: string;
		[fields.link]: LinkFieldValue;
		[fields.variant]: "primary" | "secondary" | "tertiary";
		[fields.heading]: string;
		[fields.info]: string;
		[fields.openInNewWindow]: boolean;
		[fields.customCSS]: string;
	};
}

type Props = OwnProps & WidgetProps;

export interface CallToActionStyles {
	heading?: InjectableCss;
	ctaWrapper?: InjectableCss;
	buttonStyle?: InjectableCss;
	ctaInfo?: InjectableCss;
}

export const callToActionStyles: CallToActionStyles = {
	ctaWrapper: {
		css: css`
			font-family: 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif;
			margin-bottom: 48px;
		`
	},
	heading: {
		css: css`
			font-size: 18px;
			font-weight: 700;
			text-transform: uppercase;
			letter-spacing: 1px;
			color: ${WhitecapColors.lightBlack};
			border-bottom: 1px solid #d0d3d4;
			margin-bottom: 48px;
			line-height: 24px;
			padding-bottom: 12px;
		`
	},
	buttonStyle: {
		css: css`
			span{
				font-family: 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif;
				font-size: 14px;
				line-height: 20px;
				font-weight: 400;
				text-transform: uppercase;
			}
		`
	},
	ctaInfo: {
		css: css`
			font-size: 14px;
			font-weight: 400;
			line-height: 24px;
			color: ${WhitecapColors.lightBlack};
		`
	}

};

const styles = callToActionStyles;

const customCssWrapper = {
	css: css`
            ${fields.customCSS}
        `,
};

const callToActionWidget: React.FC<Props> = ({ fields }: Props) => {
	const history = useHistory();
	const link = useGetLink(fields.link);
	const { isInShell } = useShellContext();

	const onClick = () => {
		if (!link.url) {
			return;
		}

		if (fields.openInNewWindow) {
			window.open(link.url, '_blank');
			return;
		}

		if (link.url.startsWith("http")) {
			if (isInShell) {
				return;
			}

			window.location.href = link.url;
		} else {
			history.push(link.url);
		}
	}
	return (
		<StyledWrapper  {...customCssWrapper} >
			<StyledWrapper {...styles.ctaWrapper}>
				<StyledWrapper className="call-to-action-heading">
					<Typography as="h2" {...styles.heading}>{fields.heading}</Typography>
				</StyledWrapper>
				<GridContainer>
					<GridItem width={[12, 12, 12, 12, 9]} >
						<Typography {...styles.ctaInfo} as="p">{fields.info}</Typography>
					</GridItem>
					{fields.label && (
						<GridItem width={[12, 12, 12, 12, 3]} >
							<Button className="cta-button" {...styles.buttonStyle} variant={fields.variant} onClick={onClick}>
								{fields.label}
							</Button>
						</GridItem>
					)}
				</GridContainer>
			</StyledWrapper>
		</StyledWrapper>
	);
};

const defaultCustomCss = `.wrapper {
}

.button {
}
`;

const basicTab = {
	displayName: "Basic",
	sortOrder: 0,
};

const advancedTab = {
	displayName: "Advanced",
	sortOrder: 1,
};


const widgetModule: WidgetModule = {
	component: callToActionWidget,
	definition: {
		group: "Basic",
		displayName: "Call To Action Widget (CTA)",
		fieldDefinitions: [

			{
				name: fields.heading,
				displayName: "Heading",
				editorTemplate: "TextField",
				defaultValue: "",
				fieldType: "General",
				tab: basicTab,
			},
			{
				name: fields.info,
				displayName: "Description",
				editorTemplate: "TextField",
				defaultValue: "",
				fieldType: "General",
				tab: basicTab,
			},
			{
				name: fields.variant,
				displayName: "Button Variant",
				editorTemplate: "DropDownField",
				options: [
					{ displayName: "Primary", value: "primary" },
					{ displayName: "Secondary", value: "secondary" },
					{ displayName: "Tertiary", value: "tertiary" },
				],
				defaultValue: "primary",
				fieldType: "General",
				tab: basicTab,
			},
			{
				name: fields.label,
				displayName: "Button Label",
				editorTemplate: "TextField",
				defaultValue: "",
				fieldType: "Translatable",
				tab: basicTab,
			},
			{
				name: fields.link,
				displayName: "Button Link",
				editorTemplate: "LinkField",
				defaultValue: {
					value: "",
					type: "Page",
				},
				fieldType: "General",
				tab: basicTab,
			},
			{
				name: fields.openInNewWindow,
				displayName: "Open in New Window",
				editorTemplate: "CheckboxField",
				defaultValue: false,
				fieldType: "General",
				tab: basicTab,
			},
			{
				name: fields.customCSS,
				displayName: "Custom CSS",
				editorTemplate: "CodeField",
				fieldType: "General",
				tab: advancedTab,
				defaultValue: defaultCustomCss,
				isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
				validate: value => {
					if (value === undefined || value === null) {
						return "";
					}

					const result = cssLinter.parse(value, { silent: true });
					if (result?.stylesheet?.parsingErrors) {
						// the error output at this time only has room for one line so we just show the first error
						return result.stylesheet.parsingErrors.length <= 0
							? ""
							: result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
					}

					return "Unable to parse Css";
				},
				options: {
					mode: "css",
					lint: true,
					autoRefresh: true,
				},
			},

		],

	}

};

export default widgetModule;
